/**
 * Pure host classifier — dipisah dari HostContext.jsx supaya bisa diuji unit
 * tanpa dependency React/axios. Kembali `{ kind, slug?, customDomain? }`.
 *
 * Aturan:
 *   - "platform": app.dynicty.com, subdomain reserved (api/admin/www/…),
 *     preview/dev hosts (*.preview.emergentagent.com, localhost, 127.0.0.1).
 *   - "tenant" (subdomain): <slug>.dynicty.com dengan slug valid → resolusi
 *     tenant via /api/public/tenant-by-host (slug matching).
 *   - "tenant" (customDomain=true): FQDN eksternal (bukan platform) → resolusi
 *     tenant via /api/public/tenant-by-host (binding tenant_domains ACTIVE).
 *     Fail-closed & cross-tenant isolation ditegakkan di server.
 *   - "unknown": bare apex dynicty.com, single-label host, IP literal, TLD
 *     tidak valid, atau input kosong/malformed.
 */

export const PLATFORM_APEX = "dynicty.com";
export const PLATFORM_HOST = "app.dynicty.com";
export const RESERVED_SUBDOMAINS = new Set([
  "app", "api", "admin", "www", "cdn", "static", "media", "assets",
]);
export const DEV_HOST_SUFFIXES = [
  ".preview.emergentagent.com",
  ".emergentagent.com",
  "localhost",
  "127.0.0.1",
];

// FQDN pattern: mengikuti backend normalize_hostname (services/domain_service.py).
// Minimal length 4, maksimal 253, TLD 2..24 huruf.
const FQDN_RE = /^(?=.{4,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,24}$/;
const IPV4_RE = /^\d{1,3}(\.\d{1,3}){3}$/;
const SLUG_RE = /^[a-z0-9][a-z0-9-]{2,39}$/;

export function isDevHost(host) {
  const h = (host || "").toLowerCase();
  return DEV_HOST_SUFFIXES.some(
    (suf) => h === suf.replace(/^\./, "") || h.endsWith(suf),
  );
}

export function classifyHost(host) {
  const h = (host || "").toLowerCase();
  // Platform + dev/preview hosts (owner portal / lokal).
  if (h === PLATFORM_HOST) return { kind: "platform" };
  if (isDevHost(h)) return { kind: "platform" };
  // Subdomain resmi Dynicty (*.dynicty.com).
  const apexSuffix = "." + PLATFORM_APEX;
  if (h.endsWith(apexSuffix)) {
    const label = h.slice(0, -apexSuffix.length);
    if (!label || label.includes(".")) return { kind: "unknown" };
    if (RESERVED_SUBDOMAINS.has(label)) return { kind: "platform" };
    if (!SLUG_RE.test(label)) return { kind: "unknown" };
    return { kind: "tenant", slug: label, customDomain: false };
  }
  // Bare apex platform.
  if (h === PLATFORM_APEX) return { kind: "unknown" };
  // Custom domain candidate: harus FQDN valid (bukan IP, bukan label tunggal).
  if (!h || IPV4_RE.test(h) || !FQDN_RE.test(h)) return { kind: "unknown" };
  return { kind: "tenant", slug: null, customDomain: true };
}
